import { callEndpoint } from 'redux/global';
import { IResponse } from 'types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'redux/store';
import { dbFetchPageQuery } from 'utils/dbFetchPage';
import { Log } from './hooks';
import { Metric } from 'pages/redux';
import { withLiveDemoSessionLog } from './liveDemoSessionLog';
import {
  ActivityGroup,
  ActivityGroupLink,
  ActivityOrganizationRef,
  migrateActivityGroupsChecked,
  SHOW_ALL_ACTIVITY_LOGS,
} from './activityGroupsModel';
import type { ActivityLogsSearchPayload } from './activityLogsSearchPayload';

export type LogSheetEntry = {
  id: number,
  message: string,
  value: number,
  date: string,
  updatedBy: string,
  updatedAsOf: string,
  sheetName: string,
}

export type BranchFile = Record<string, LogSheetEntry[]>;
export type BranchFiles = Record<string, BranchFile>

/** Legacy type names kept for Metrics / Reporting shims — not left-pane source of truth. */
export const logTypes0 = ['Staff logged in',
  'Settings change',
  'Staff added', 'Staff removed', 'Staff change', 'Log deleted',
  'Log change', 'Log restored', 'Inventory Added', 'Inventory Renamed', 'Inventory deleted',
  'Direct', 'Expense', 'Document',
  'Compliance favorable', 'Compliance note', 'Compliance incident', 'Compliance alert', 'Report sent', 'Report filed', 'Report closed',
  'Department added', 'Department renamed', 'Department removed', 'Department change',
  'Workflow added', 'Workflow changed', 'Workflow published', 'Workflow archived', 'Workflow removed',
  'Workflow task', 'Workflow approval', 'Workflow escalation',
  'Workflow task done', 'Workflow approved', 'Workflow rejected', 'Workflow closed',
  'Record added', 'Record change', 'Record removed', 'Employee invite sent', 'Form submitted',
  'Designation employees rotated', 'Manager performance standard seeded'] as const;

export const logKeys0 = ['Departments', 'Department entries', 'Inventory', 'Inventory Group', 'Actions', 'Show all'];

/** Default: Show all once groups load. */
const defaultChecked = JSON.stringify([SHOW_ALL_ACTIVITY_LOGS]);

const migrateLogsChecked = (checked: string[]): string[] => migrateActivityGroupsChecked(checked);

export type LogsValueMode = 'value' | 'units';

/** /logs type column: department / inventory group / SKU. */
export type ActivityLogsTypeAttributeMode = 'department' | 'inventory_group' | 'sku';

const normalizeStoredActivityLogsTypeAttributeMode = (
  mode: string | null | undefined,
): ActivityLogsTypeAttributeMode => {
  if (mode === 'sku') return 'sku';
  if (mode === 'inventory_group') return 'inventory_group';
  // Legacy rollup → department.
  return 'department';
};

/** Bottom search-mode radios: exact tagged match (room / staff / customer / department / ota). */
export type LogSearchMatchMode = 'any' | 'room' | 'staff' | 'customer' | 'department' | 'ota';

/** Draft / per-chip activity search settings (dropdown). */
export type LogSearchOptions = {
  /** Require full-term equality (no substring / slice). */
  exactMatch: boolean,
  /** Exclude matching results. */
  negate: boolean,
  /** Skip archived inventory items, groups, and deleted departments. */
  hideArchives: boolean,
  /**
   * 2nd+ search only: match against the log results from before the previous
   * search (not the original full log list) and union those hits into the
   * last results. External filters still apply after search.
   */
  fromFullLogs: boolean,
  /** Exact tagged match; `any` = general details search. */
  matchMode: LogSearchMatchMode,
};

export const DEFAULT_LOG_SEARCH_OPTIONS: LogSearchOptions = {
  exactMatch: false,
  negate: false,
  hideArchives: false,
  fromFullLogs: false,
  matchMode: 'any',
};

export const normalizeLogSearchOptions = (
  partial?: (Partial<LogSearchOptions> & {
    /** Legacy per-kind archive flags — any one turns hideArchives on. */
    hideTypeArchives?: boolean,
    hideGroupArchives?: boolean,
    hideDepartmentArchives?: boolean,
  }) | null,
): LogSearchOptions => {
  const mode = partial?.matchMode;
  return {
    exactMatch: Boolean(partial?.exactMatch),
    negate: Boolean(partial?.negate),
    hideArchives: Boolean(
      partial?.hideArchives
      || partial?.hideTypeArchives
      || partial?.hideGroupArchives
      || partial?.hideDepartmentArchives,
    ),
    fromFullLogs: Boolean(partial?.fromFullLogs),
    matchMode: mode === 'room' || mode === 'staff' || mode === 'customer'
      || mode === 'department' || mode === 'ota'
      ? mode
      : 'any',
  };
};

/** One committed (or draft-shaped) activity search. */
export type LogSearchFilter = {
  term: string,
  options: LogSearchOptions,
};

export interface LogsState {
  logs: Log[] | null,
  nonBranchLogs: Log[] | null,
  singleLogTime: Metric | null,
  /** Groups (activity scopes) left-pane selection. */
  checked: string[],
  recentLog: string,
  branchFiles: BranchFiles | null,
  /** Active draft for the activityLogs selector search input. */
  logSearch: string,
  /** Draft search options (dropdown). */
  logSearchOptions: LogSearchOptions,
  /**
   * Committed multi-search chips (max 5). Default: each narrows the previous
   * remainder. With `fromFullLogs` (2nd+): union matches from the results
   * before the previous search.
   */
  logSearches: LogSearchFilter[],
  isLoadingLogs: boolean,
  /** Associate Manager unlock: see higher-role activityLogs (Compliance review only). */
  associateElevatedActivityLogs: boolean,
  /**
   * Compliance → associated activityLogs: after the compliance cashier's first entry,
   * hide other users' logs. Cleared when time range is turned off or changed.
   */
  isComplianceActivity: boolean,
  /** Staff id of the Compliance cashier (preferred match key). */
  complianceActivityStaffId: string | null,
  /** Username of the Compliance cashier (legacy / fallback match). */
  complianceActivityUsername: string | null,
  /**
   * Temporary ActivityLogs identity from a staff report "Show logs" jump —
   * the other party's item uses their visibility. Own items stay as you.
   * Never elevates above the signed-in user's real permission.
   */
  activityLogsViewAs: {
    permission: number,
    staffId: string | null,
    username: string | null,
    label?: string | null,
  } | null,
  /**
   * Staff-report "Show all logs": keep only these activity_logs ids
   * (one activity set). Cleared when leaving ActivityLogs or turning Time off.
   */
  activityLogsOnlyLogIds: string[] | null,
  /** Report jump: first log in the clicked set (inclusive). */
  activityLogsStartLogId: string | null,
  /** Report jump: last log in the clicked set (inclusive). */
  activityLogsEndLogId: string | null,
  /** Roll up value logs by SKU/type in the ActivityLogs table. */
  activityLogsCombineSku: boolean,
  /**
   * ActivityLogs Time toggle. Reporting must follow this, not a leftover
   * singleLogTime from an earlier visit (Show all used to hide Time
   * without clearing the range).
   */
  activityLogsUseTimeRange: boolean,
  /** Cash-only off — include N/A rows. Persists into Reporting / Metrics. */
  activityLogsAllItems: boolean,
  /** Cash-In toggle when Cash-only is on. */
  activityLogsCashIn: boolean,
  /** Cash-Out toggle when Cash-only is on. */
  activityLogsCashOut: boolean,
  /** Value column mode in /logs (Compliance can jump to Units). */
  logsValueMode: LogsValueMode,
  /**
   * When a department or inventory group is selected, how temporary type
   * labels attribute rows (department / inventory_group / sku).
   */
  activityLogsTypeAttributeMode: ActivityLogsTypeAttributeMode,
  /** Activity scopes from getactivitygroups (groupTypes from linked_skus tables). */
  activityGroups: ActivityGroup[],
  /** Parent → child group edges for nested activity scopes. */
  activityLinks: ActivityGroupLink[],
  /** Every organization row — Organization scope children on /activity. */
  activityOrganizations: ActivityOrganizationRef[],
  /** Unfiltered structural erp rows for head_scope → department mapping on /logs. */
  activityHeadScopeErpRecords: Array<{
    id?: string | null,
    kind?: string | null,
    name?: string | null,
    parent_id?: string | null,
    parentId?: string | null,
  }>,
  /** Master-data / BO partition source logs from getactivitygroups (not search subset). */
  activityPartitionLogs: Array<Record<string, unknown>>,
  isLoadingActivityGroups: boolean,
  /**
   * /logs right pane: idle prompt, in-page loader, or table.
   * Logs are not fetched until Search / Latest / a report jump.
   */
  activityLogsSearchStatus: 'idle' | 'loading' | 'results',
  /** False when the last searchlogs page returned fewer rows than pageSize. */
  activityLogsSearchHasMore: boolean,
  /** Current /logs paginator page — kept when leaving and coming back. */
  activityLogsSearchPage: number,
  /** Highest paginator page fetched this search (keeps 1…N visible). */
  activityLogsSearchLoadedPageCount: number,
  /** Reporting → /logs: run Search with the current left-pane criteria. */
  activityLogsSearchQueued: boolean,
  /** Last activity scope turned on in the left pane (summary lead). */
  activityLogsLastSelectedScopeId: string | null,
  /** Shared drill path (node key) under nested activity scopes. */
  activityLogsOpenScopeBranchKey: string | null,
  /** All downloaded search pages merged — shared by /metrics and analytics. */
  activityLogsMergedLogs: Log[] | null,
}

const initialCheckedRaw = migrateLogsChecked(
  JSON.parse(localStorage.getItem('logs_checked') || defaultChecked)
);
// Cash-session jumps used to write legacy name tokens; migrate strips them to [].
const initialChecked = initialCheckedRaw.length
  ? initialCheckedRaw
  : [SHOW_ALL_ACTIVITY_LOGS];
localStorage.setItem('logs_checked', JSON.stringify(initialChecked));

const ACTIVITY_LOGS_SEARCH_PAGE_KEY = 'activityLogs_search_page';
const ACTIVITY_LOGS_SEARCH_LOADED_PAGE_COUNT_KEY = 'activityLogs_search_loaded_page_count';

const readStoredPositiveInt = (key: string, fallback: number) => {
  try {
    const n = Math.floor(Number(localStorage.getItem(key)));
    return Number.isFinite(n) && n >= 1 ? n : fallback;
  } catch {
    return fallback;
  }
};

const persistActivityLogsSearchPaginator = (page: number, loadedPageCount: number) => {
  localStorage.setItem(ACTIVITY_LOGS_SEARCH_PAGE_KEY, String(page));
  localStorage.setItem(ACTIVITY_LOGS_SEARCH_LOADED_PAGE_COUNT_KEY, String(loadedPageCount));
};

const initialActivityLogsSearchPage = readStoredPositiveInt(ACTIVITY_LOGS_SEARCH_PAGE_KEY, 1);
const initialActivityLogsSearchLoadedPageCount = Math.max(
  initialActivityLogsSearchPage,
  readStoredPositiveInt(ACTIVITY_LOGS_SEARCH_LOADED_PAGE_COUNT_KEY, 1),
);
persistActivityLogsSearchPaginator(initialActivityLogsSearchPage, initialActivityLogsSearchLoadedPageCount);

const LOGS_SEARCHES_KEY = 'logs_searches';
const LOGS_SEARCH_KEY = 'logs_search';
const LOGS_SEARCH_OPTIONS_KEY = 'logs_search_options';
const LOGS_SEARCH_NEGATE_KEY = 'logs_search_negate';

const parseFilterOptions = (f: any): LogSearchOptions => {
  if (f?.options && typeof f.options === 'object') {
    return normalizeLogSearchOptions(f.options);
  }
  // Legacy chips stored only `negate`.
  return normalizeLogSearchOptions({
    ...DEFAULT_LOG_SEARCH_OPTIONS,
    negate: Boolean(f?.negate),
  });
};

const parseStoredSearches = (): LogSearchFilter[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(LOGS_SEARCHES_KEY) || '[]');
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((f) => f && typeof f.term === 'string' && f.term.trim())
      .slice(0, 5)
      .map((f) => ({
        term: String(f.term).trim(),
        options: parseFilterOptions(f),
      }));
  } catch {
    return [];
  }
};

const initialLogSearches = parseStoredSearches();
const initialLogSearch = (() => {
  try {
    return String(localStorage.getItem(LOGS_SEARCH_KEY) || '');
  } catch {
    return '';
  }
})();
const initialLogSearchOptions = (() => {
  try {
    const raw = localStorage.getItem(LOGS_SEARCH_OPTIONS_KEY);
    if (raw) {
      return normalizeLogSearchOptions(JSON.parse(raw));
    }
    // Migrate legacy negate-only key.
    if (localStorage.getItem(LOGS_SEARCH_NEGATE_KEY) === '1') {
      return normalizeLogSearchOptions({ ...DEFAULT_LOG_SEARCH_OPTIONS, negate: true });
    }
  } catch {
    // fall through
  }
  return { ...DEFAULT_LOG_SEARCH_OPTIONS };
})();

const persistLogSearches = (searches: LogSearchFilter[]) => {
  localStorage.setItem(LOGS_SEARCHES_KEY, JSON.stringify(searches));
};

const persistLogSearch = (search: string) => {
  localStorage.setItem(LOGS_SEARCH_KEY, search);
};

const persistLogSearchOptions = (options: LogSearchOptions) => {
  localStorage.setItem(LOGS_SEARCH_OPTIONS_KEY, JSON.stringify(options));
  localStorage.setItem(LOGS_SEARCH_NEGATE_KEY, options.negate ? '1' : '0');
};

const initialState: LogsState = {
  logs: null,
  nonBranchLogs: null,
  singleLogTime: null,
  checked: initialChecked,
  recentLog: '',
  branchFiles: null,
  logSearch: initialLogSearch,
  logSearchOptions: initialLogSearchOptions,
  logSearches: initialLogSearches,
  isLoadingLogs: false,
  associateElevatedActivityLogs: false,
  isComplianceActivity: false,
  complianceActivityStaffId: null,
  complianceActivityUsername: null,
  activityLogsViewAs: null,
  activityLogsOnlyLogIds: null,
  activityLogsStartLogId: null,
  activityLogsEndLogId: null,
  activityLogsCombineSku: false,
  activityLogsUseTimeRange: false,
  activityLogsAllItems: true,
  activityLogsCashIn: true,
  activityLogsCashOut: true,
  logsValueMode: 'value',
  activityLogsTypeAttributeMode: 'department',
  activityGroups: [],
  activityLinks: [],
  activityOrganizations: [],
  activityHeadScopeErpRecords: [],
  activityPartitionLogs: [],
  isLoadingActivityGroups: false,
  activityLogsSearchStatus: 'idle',
  activityLogsSearchHasMore: false,
  activityLogsSearchPage: initialActivityLogsSearchPage,
  activityLogsSearchLoadedPageCount: initialActivityLogsSearchLoadedPageCount,
  activityLogsSearchQueued: false,
  activityLogsLastSelectedScopeId: null,
  activityLogsOpenScopeBranchKey: null,
  activityLogsMergedLogs: null,
};

export const counterSlice = createSlice({
  name: 'logs',
  initialState,
  reducers: {
    setLogs: (state, action: PayloadAction<Log[] | null>) => {
      state.logs = action.payload;
    },
    setSingleLogTime: (state, action: PayloadAction<Metric | null>) => {
      state.singleLogTime = action.payload;
      state.activityLogsUseTimeRange = Boolean(
        action.payload?.startDate && action.payload?.endDate,
      );
      if (!action.payload) {
        state.associateElevatedActivityLogs = false;
        state.isComplianceActivity = false;
        state.complianceActivityStaffId = null;
        state.complianceActivityUsername = null;
        // View-as ends with the timed jump; onlyLogIds stay until Exit / leave /logs
        // (LogTable Time-off used to clear the pin and cash-session ₦0 became a broad search).
        state.activityLogsViewAs = null;
      }
    },
    setLogsChecked: (state, action: PayloadAction<string[]>) => {
      const migrated = migrateLogsChecked(action.payload);
      // Allow empty selection so “Show all” and head-scope checkboxes can toggle off.
      // (UI handles empty by prompting the user to select activity types.)
      state.checked = migrated;
      localStorage.setItem('logs_checked', JSON.stringify(migrated));
    },
    /** Mobile overlay / empty-table: same as Groups selection. */
    setActivityLogsShowAllSelection: (state, action: PayloadAction<string[]>) => {
      const migrated = migrateLogsChecked(action.payload);
      state.checked = migrated;
      localStorage.setItem('logs_checked', JSON.stringify(migrated));
    },
    setRecentLog: (state, action: PayloadAction<string>) => {
      state.recentLog = action.payload;
    },
    setBranchFiles: (state, action: PayloadAction<BranchFiles | null>) => {
      state.branchFiles = action.payload;
    },
    setNonBranchLogs: (state, action: PayloadAction<Log[] | null>) => {
      state.nonBranchLogs = action.payload;
    },
    setLogSearch: (state, action: PayloadAction<string>) => {
      state.logSearch = action.payload;
      persistLogSearch(action.payload);
    },
    setLogSearchOptions: (state, action: PayloadAction<LogSearchOptions | Partial<LogSearchOptions>>) => {
      state.logSearchOptions = normalizeLogSearchOptions({
        ...state.logSearchOptions,
        ...action.payload,
      });
      persistLogSearchOptions(state.logSearchOptions);
    },
    setLogSearches: (state, action: PayloadAction<LogSearchFilter[]>) => {
      state.logSearches = action.payload.slice(0, 5).map((f) => ({
        term: f.term,
        options: normalizeLogSearchOptions(f.options),
      }));
      persistLogSearches(state.logSearches);
    },
    setIsLoadingLogs: (state, action: PayloadAction<boolean>) => {
      state.isLoadingLogs = action.payload;
    },
    setAssociateElevatedActivityLogs: (state, action: PayloadAction<boolean>) => {
      state.associateElevatedActivityLogs = action.payload;
    },
    setComplianceActivity: (state, action: PayloadAction<{
      active: boolean,
      staffId?: string | null,
      username?: string | null,
    }>) => {
      const { active, staffId, username } = action.payload;
      state.isComplianceActivity = active;
      if (!active) {
        state.complianceActivityStaffId = null;
        state.complianceActivityUsername = null;
        return;
      }
      state.complianceActivityStaffId = staffId != null && String(staffId) !== ''
        ? String(staffId)
        : null;
      state.complianceActivityUsername = username?.trim() || null;
    },
    setLogsValueMode: (state, action: PayloadAction<LogsValueMode>) => {
      state.logsValueMode = action.payload === 'units' ? 'units' : 'value';
    },
    setActivityLogsTypeAttributeMode: (state, action: PayloadAction<ActivityLogsTypeAttributeMode | 'rollup'>) => {
      state.activityLogsTypeAttributeMode = normalizeStoredActivityLogsTypeAttributeMode(action.payload);
    },
    setActivityLogsViewAs: (state, action: PayloadAction<{
      permission: number,
      staffId?: string | null,
      username?: string | null,
      label?: string | null,
    } | null>) => {
      if (!action.payload) {
        state.activityLogsViewAs = null;
        return;
      }
      const permission = Number(action.payload.permission);
      if (!Number.isFinite(permission) || permission < 0) {
        state.activityLogsViewAs = null;
        return;
      }
      state.activityLogsViewAs = {
        permission: Math.floor(permission),
        staffId: action.payload.staffId != null && String(action.payload.staffId)
          ? String(action.payload.staffId)
          : null,
        username: action.payload.username != null && String(action.payload.username).trim()
          ? String(action.payload.username).trim()
          : null,
        label: action.payload.label != null && String(action.payload.label).trim()
          ? String(action.payload.label).trim().slice(0, 80)
          : null,
      };
    },
    setActivityLogsOnlyLogIds: (state, action: PayloadAction<string[] | null>) => {
      if (!action.payload?.length) {
        state.activityLogsOnlyLogIds = null;
        return;
      }
      const ids = action.payload.map((id) => String(id || '').trim()).filter(Boolean);
      state.activityLogsOnlyLogIds = ids.length ? ids : null;
    },
    setActivityLogsStartEndLogIds: (state, action: PayloadAction<{
      startLogId?: string | null,
      endLogId?: string | null,
    } | null>) => {
      const start = String(action.payload?.startLogId || '').trim();
      const end = String(action.payload?.endLogId || '').trim();
      state.activityLogsStartLogId = start || null;
      state.activityLogsEndLogId = end || null;
    },
    setActivityLogsCombineSku: (state, action: PayloadAction<boolean>) => {
      state.activityLogsCombineSku = Boolean(action.payload);
    },
    setActivityLogsUseTimeRange: (state, action: PayloadAction<boolean>) => {
      state.activityLogsUseTimeRange = Boolean(action.payload);
      if (!action.payload) {
        state.singleLogTime = null;
      }
    },
    setActivityLogsCashFilters: (state, action: PayloadAction<{
      allItems?: boolean,
      cashIn?: boolean,
      cashOut?: boolean,
    }>) => {
      if (typeof action.payload.allItems === 'boolean') {
        state.activityLogsAllItems = action.payload.allItems;
      }
      if (typeof action.payload.cashIn === 'boolean') {
        state.activityLogsCashIn = action.payload.cashIn;
      }
      if (typeof action.payload.cashOut === 'boolean') {
        state.activityLogsCashOut = action.payload.cashOut;
      }
    },
    setActivityGroupsPayload: (state, action: PayloadAction<{
      groups: ActivityGroup[],
      links?: ActivityGroupLink[],
      organizations?: ActivityOrganizationRef[],
      headScopeErpRecords?: Array<{
        id?: string | null,
        kind?: string | null,
        name?: string | null,
        parent_id?: string | null,
        parentId?: string | null,
      }>,
      partitionLogs?: Array<Record<string, unknown>>,
    }>) => {
      const prevFp = (state.activityGroups || []).map((g) => g.id).sort().join(',');
      const nextGroups = action.payload.groups || [];
      const nextFp = nextGroups.map((g) => g.id).sort().join(',');
      state.activityGroups = nextGroups;
      state.activityLinks = action.payload.links || [];
      state.activityOrganizations = action.payload.organizations || [];
      state.activityHeadScopeErpRecords = action.payload.headScopeErpRecords || [];
      state.activityPartitionLogs = action.payload.partitionLogs || [];
      // Reseed / scope reload — drop stale drill + scopes-tab checked UUIDs.
      if (prevFp && nextFp !== prevFp) {
        state.activityLogsOpenScopeBranchKey = null;
        state.checked = [SHOW_ALL_ACTIVITY_LOGS];
        localStorage.setItem('logs_checked', JSON.stringify(state.checked));
      }
    },
    setIsLoadingActivityGroups: (state, action: PayloadAction<boolean>) => {
      state.isLoadingActivityGroups = Boolean(action.payload);
    },
    setActivityLogsSearchStatus: (state, action: PayloadAction<'idle' | 'loading' | 'results'>) => {
      state.activityLogsSearchStatus = action.payload;
    },
    setActivityLogsSearchHasMore: (state, action: PayloadAction<boolean>) => {
      state.activityLogsSearchHasMore = Boolean(action.payload);
    },
    setActivityLogsSearchPage: (state, action: PayloadAction<number>) => {
      const page = Math.max(1, Math.floor(Number(action.payload) || 1));
      state.activityLogsSearchPage = page;
      state.activityLogsSearchLoadedPageCount = Math.max(state.activityLogsSearchLoadedPageCount, page);
      persistActivityLogsSearchPaginator(state.activityLogsSearchPage, state.activityLogsSearchLoadedPageCount);
    },
    /** Metrics download arrow — cache another page without moving /logs paginator. */
    appendActivityLogsSearchLoadedPage: (state, action: PayloadAction<number>) => {
      const page = Math.max(1, Math.floor(Number(action.payload) || 1));
      state.activityLogsSearchLoadedPageCount = Math.max(state.activityLogsSearchLoadedPageCount, page);
      persistActivityLogsSearchPaginator(state.activityLogsSearchPage, state.activityLogsSearchLoadedPageCount);
    },
    resetActivityLogsSearchPaginator: (state) => {
      state.activityLogsSearchPage = 1;
      state.activityLogsSearchLoadedPageCount = 1;
      persistActivityLogsSearchPaginator(1, 1);
    },
    setActivityLogsSearchQueued: (state, action: PayloadAction<boolean>) => {
      state.activityLogsSearchQueued = Boolean(action.payload);
    },
    setActivityLogsLastSelectedScopeId: (state, action: PayloadAction<string | null>) => {
      const id = String(action.payload || '').trim();
      state.activityLogsLastSelectedScopeId = id || null;
    },
    setActivityLogsOpenScopeBranchKey: (state, action: PayloadAction<string | null>) => {
      const key = String(action.payload || '').trim();
      state.activityLogsOpenScopeBranchKey = key || null;
    },
    setActivityLogsMergedLogs: (state, action: PayloadAction<Log[] | null>) => {
      state.activityLogsMergedLogs = action.payload;
    },
  },
});

export const {
  setLogs, setSingleLogTime, setLogsChecked, setActivityLogsShowAllSelection, setRecentLog,
  setBranchFiles,
  setNonBranchLogs,
  setLogSearch,
  setLogSearchOptions,
  setLogSearches,
  setIsLoadingLogs,
  setAssociateElevatedActivityLogs,
  setComplianceActivity,
  setLogsValueMode,
  setActivityLogsTypeAttributeMode,
  setActivityLogsViewAs,
  setActivityLogsOnlyLogIds,
  setActivityLogsStartEndLogIds,
  setActivityLogsCombineSku,
  setActivityLogsUseTimeRange,
  setActivityLogsCashFilters,
  setActivityGroupsPayload,
  setIsLoadingActivityGroups,
  setActivityLogsSearchStatus,
  setActivityLogsSearchHasMore,
  setActivityLogsSearchPage,
  appendActivityLogsSearchLoadedPage,
  resetActivityLogsSearchPaginator,
  setActivityLogsSearchQueued,
  setActivityLogsLastSelectedScopeId,
  setActivityLogsOpenScopeBranchKey,
  setActivityLogsMergedLogs,
} = counterSlice.actions;

export const selectLogs = (state: RootState) => state.logs.logs;
export const selectNonBranchLogs = (state: RootState) => state.logs.nonBranchLogs;
export const selectSingleLogTime = (state: RootState) => state.logs.singleLogTime;
/** Groups left-pane checkbox selection. */
export const selectLogsChecked = (state: RootState) => state.logs.checked;
/** Search / table — same as Groups selection (SKU tab removed). */
export const selectActivityLogsCombinedChecked = (state: RootState) => state.logs.checked;
export const selectScopesChecked = (state: RootState) => state.logs.checked;
export const selectRecentLog = (state: RootState) => state.logs.recentLog;
export const selectBranchFiles = (state: RootState) => state.logs.branchFiles;
export const selectLogSearch = (state: RootState) => state.logs.logSearch;
export const selectLogSearchOptions = (state: RootState) => state.logs.logSearchOptions;
export const selectLogSearches = (state: RootState) => state.logs.logSearches;
export const selectIsLoadingLogs = (state: RootState) => state.logs.isLoadingLogs;
export const selectAssociateElevatedActivityLogs = (state: RootState) => state.logs.associateElevatedActivityLogs;
export const selectIsComplianceActivity = (state: RootState) => state.logs.isComplianceActivity;
export const selectComplianceActivityStaffId = (state: RootState) => state.logs.complianceActivityStaffId;
export const selectComplianceActivityUsername = (state: RootState) => state.logs.complianceActivityUsername;
export const selectLogsValueMode = (state: RootState) => state.logs.logsValueMode;
export const selectActivityLogsTypeAttributeMode = (state: RootState) => (
  normalizeStoredActivityLogsTypeAttributeMode(state.logs.activityLogsTypeAttributeMode)
);
export const selectActivityLogsViewAs = (state: RootState) => state.logs.activityLogsViewAs;
export const selectActivityLogsOnlyLogIds = (state: RootState) => state.logs.activityLogsOnlyLogIds;
export const selectActivityLogsStartLogId = (state: RootState) => state.logs.activityLogsStartLogId;
export const selectActivityLogsEndLogId = (state: RootState) => state.logs.activityLogsEndLogId;
export const selectActivityLogsCombineSku = (state: RootState) => state.logs.activityLogsCombineSku;
export const selectActivityLogsUseTimeRange = (state: RootState) => state.logs.activityLogsUseTimeRange;
export const selectActivityLogsAllItems = (state: RootState) => state.logs.activityLogsAllItems;
export const selectActivityLogsCashIn = (state: RootState) => state.logs.activityLogsCashIn;
export const selectActivityLogsCashOut = (state: RootState) => state.logs.activityLogsCashOut;
export const selectActivityGroups = (state: RootState) => state.logs.activityGroups;
export const selectActivityLinks = (state: RootState) => state.logs.activityLinks;
export const selectActivityOrganizations = (state: RootState) => state.logs.activityOrganizations;
export const selectActivityHeadScopeErpRecords = (state: RootState) => (
  state.logs.activityHeadScopeErpRecords
);
export const selectActivityPartitionLogs = (state: RootState) => state.logs.activityPartitionLogs;
export const selectIsLoadingActivityGroups = (state: RootState) => state.logs.isLoadingActivityGroups;
export const selectActivityLogsSearchStatus = (state: RootState) => state.logs.activityLogsSearchStatus;
export const selectActivityLogsSearchHasMore = (state: RootState) => state.logs.activityLogsSearchHasMore;
export const selectActivityLogsSearchPage = (state: RootState) => state.logs.activityLogsSearchPage;
export const selectActivityLogsSearchLoadedPageCount = (state: RootState) => (
  state.logs.activityLogsSearchLoadedPageCount
);
export const selectActivityLogsSearchQueued = (state: RootState) => state.logs.activityLogsSearchQueued;
export const selectActivityLogsLastSelectedScopeId = (state: RootState) => (
  state.logs.activityLogsLastSelectedScopeId
);
export const selectActivityLogsOpenScopeBranchKey = (state: RootState) => (
  state.logs.activityLogsOpenScopeBranchKey
);
export const selectActivityLogsMergedLogs = (state: RootState) => state.logs.activityLogsMergedLogs;
/** Report Show logs jump — pin the table until Exit. Start/end log ids are a range, not this. */
export const selectIsActivityLogsReportLogsFocus = (state: RootState) => {
  if (state.logs.activityLogsViewAs) return true;
  const only = state.logs.activityLogsOnlyLogIds;
  if (!only?.length) return false;
  // Cash sessions pin onlyLogIds + elevate without viewAs — allow /logs checkbox toggles.
  if (state.logs.associateElevatedActivityLogs) return false;
  return true;
};

/** Clicking an activity scope while report logs are pinned. */
export const ACTIVITY_LOGS_REPORT_LOGS_FOCUS_GROUPS_TOAST =
  'Exit to change activity scopes and catalog items';

/** Time / cash chips while report logs are pinned. */
export const ACTIVITY_LOGS_REPORT_LOGS_FOCUS_FILTERS_TOOLTIP =
  'Exit to change time and cash filters';

export default counterSlice.reducer;

export const getLogs = (_forceRefresh = false) => async (
  dispatch: Function,
  getState: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: `getlogs?${dbFetchPageQuery()}`,
      method: 'GET',
    }),
  );
  if (res.status === 'success' && Array.isArray(res.data)) {
    const { liveView, staffId, username, permission } = getState().global;
    res.data = withLiveDemoSessionLog(res.data, Boolean(liveView), {
      staffId,
      username,
      permission,
    });
  }
  return res;
};

export const searchActivityLogs = (payload: ActivityLogsSearchPayload) => async (
  dispatch: Function,
  getState: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'searchlogs',
      method: 'POST',
      body: payload,
    }),
  );
  if (res.status === 'success' && res.data && typeof res.data === 'object') {
    const { liveView, staffId, username, permission } = getState().global;
    const raw = res.data as { logs?: unknown, hasMore?: boolean } | unknown[];
    const rows = Array.isArray(raw) ? raw : (Array.isArray(raw.logs) ? raw.logs : []);
    const hasMore = Array.isArray(raw)
      ? rows.length >= payload.pageSize
      : Boolean(raw.hasMore);
    res.data = {
      logs: withLiveDemoSessionLog(rows, Boolean(liveView), {
        staffId,
        username,
        permission,
      }),
      hasMore,
    };
  }
  return res;
};

export type AddBranchLog = {
  type: string,
  value: string,
  message: string,
  date: Date,
  department?: string,
  inventory_type?: string,
  units?: string | number,
  inventory_item_id?: string,
  stock_start?: string | number,
  stock_left?: string | number,
}

export const addBranchLog = (logEntry: AddBranchLog) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addbranchlog',
      method: 'POST',
      body: { ...logEntry }
    }),
  );
  return res;
};

export const addBranchLogs = (logs: AddBranchLog[]) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addbranchlogs',
      method: 'POST',
      body: { logs }
    }),
  );
  return res;
};

type DeleteLog = {
  dId: string,
  type: string,
  value: string,
  skipFeedback?: boolean,
}

export const deleteLog = (logToDelete: DeleteLog) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deletelog',
      method: 'POST',
      body: { ...logToDelete }
    }),
  );
  return res;
};

type RestoreLog = {
  activityLogId: string,
}

export const restoreLog = (logToRestore: RestoreLog) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'restorelog',
      method: 'POST',
      body: { ...logToRestore }
    }),
  );
  return res;
};

type EditLog = {
  editId: string,
  addLogMessage?: string,
  value?: string,
  message?: string,
  skipFeedback?: boolean,
}

export const editLog = (logToEdit: EditLog) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'editlog',
      method: 'POST',
      body: { ...logToEdit }
    }),
  );
  return res;
};

export const addLog = (log: Omit<Omit<Log, 'id'>, 'date'>) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addlog',
      method: 'POST',
      body: { log }
    }),
  );
  return res;
};

export const getBranchFiles = () => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'getbranchfiles',
      method: 'GET',
    }),
  );
  return res;
};

export const getBranchFile = (branch: string, sheetName: string) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'getbranchfile',
      method: 'POST',
      body: { branch, sheetName }
    }),
  );
  return res;
};

export const addBranchFile = (branchFile: BranchFiles) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addbranchfile',
      method: 'POST',
      body: { branchFile }
    }),
  );
  return res;
};

/** Storage check + signed GCS PUT URL for large extracts. */
export const prepareBranchFileUpload = (
  branch: string,
  sheetName: string,
  bytes: number,
) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'preparebranchfileupload',
      method: 'POST',
      body: { branch, sheetName, bytes },
    }),
  );
  return res;
};

export const deleteBranchFile = (branch: string, sheetName: string) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deletebranchfile',
      method: 'POST',
      body: { branch, sheetName }
    }),
  );
  return res;
};

export const deleteBranchFiles = (branch: string) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deletebranchfiles',
      method: 'POST',
      body: { branch }
    }),
  );
  return res;
};

type DeleteLogType = {
  type: string,
}

export const deleteLogType = (logTypeToDelete: DeleteLogType) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deletelogtype',
      method: 'POST',
      body: { ...logTypeToDelete }
    }),
  );
  return res;
};

export type RenameDepartmentInLogs = {
  from: string,
  to: string,
}

export const renameDepartmentInLogs = (payload: RenameDepartmentInLogs) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'renamedepartmentinlogs',
      method: 'POST',
      body: { ...payload },
    }),
  );
  return res;
};

export const getActivityGroups = (organizationId?: string | number | null) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'getactivitygroups',
      method: 'POST',
      body: organizationId != null && String(organizationId) !== ''
        ? { organizationId: String(organizationId) }
        : {},
    }),
  );
  return res;
};
